/**
 * Starfield — a quiet, slow-twinkling field of champagne pinpoints.
 *
 * Designed to live behind content on `surface-night` sections of the home page.
 * Stars are deterministic per `seed` so the layout never reshuffles between
 * renders. About 1/3 of stars twinkle on a long, randomised cycle (4 - 9s).
 *
 * Strictly subordinate to content:
 *   - Sits behind everything (z-index 0), pointer-events: none
 *   - Opacities capped low (cap ~ 0.7 on max twinkle)
 *   - Pinpoints are 0.5px - 2px
 *   - prefers-reduced-motion disables the twinkle (stars stay static)
 *
 * Props
 *   density   "low" | "medium" | "high"   (default "medium")
 *   topBias   number 0..1                 (1 = much denser at the top)
 *   className extra wrapper classes
 *   seed      number, default 1           (PRNG seed)
 */
import "./starfield.css";

function mulberry32(a) {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const DENSITY_COUNT = { low: 90, medium: 180, high: 280 };

export function Starfield({ density = "medium", topBias = 0.6, className = "", seed = 1 }) {
  const count = DENSITY_COUNT[density] ?? DENSITY_COUNT.medium;
  const rand = mulberry32(seed);

  const stars = Array.from({ length: count }).map(() => {
    // top-biased y: bias the distribution upward so the upper section is denser
    const u = rand();
    const y = Math.pow(u, 1 + topBias) * 100;
    const x = rand() * 100;
    const r = 0.4 + rand() * 1.4; // px
    const baseOp = 0.18 + rand() * 0.32; // 0.18 - 0.5
    const twinkles = rand() < 0.34;
    const dur = 4 + rand() * 5; // 4 - 9s
    const delay = -rand() * dur;
    // small color variation: most warm, a few brighter
    const hue = rand() < 0.85 ? "#E8D9B8" : "#FFF4D6";
    return { x, y, r, baseOp, twinkles, dur, delay, hue };
  });

  return (
    <div
      aria-hidden="true"
      className={`starfield pointer-events-none absolute inset-0 overflow-hidden ${className}`}
      data-testid="starfield"
    >
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full"
      >
        {stars.map((s, i) => (
          <circle
            key={i}
            cx={s.x}
            cy={s.y}
            r={s.r / 12} /* viewBox is 100x100, keep visually small */
            fill={s.hue}
            opacity={s.baseOp}
            className={s.twinkles ? "star-twinkle" : undefined}
            style={
              s.twinkles
                ? { animationDuration: `${s.dur}s`, animationDelay: `${s.delay}s` }
                : undefined
            }
          />
        ))}
      </svg>
    </div>
  );
}
